import styled from 'styled-components';
import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Button, Chip, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ORDER_STATUSES } from '@ubeya/shared/constants';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import * as sharedAtoms from '@ubeya/shared/atoms/shared';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { SORT_BY_DATE, SORT_BY_NUMBER, SORT_BY_STRING } from '@ubeya/shared/utils/sorting';

const Wrapper = styled.div`
  height: 100%;
`;

const SORT_MAP = {
  name: SORT_BY_STRING('name'),
  date: SORT_BY_DATE('date'),
  branchName: SORT_BY_STRING('branchName'),
  clientName: SORT_BY_STRING('clientName'),
  needed: SORT_BY_NUMBER('needed'),
  orderStatus: SORT_BY_NUMBER('orderStatus.slug'),
  createdAt: SORT_BY_DATE('createdAt')
};

const OrdersSummaryTable = ({ data = [], showBranch = false }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();

  const selectedBranchId = useRecoilValue(sharedAtoms.selectedBranch);

  const { shortDateFormat, timeFormat } = useContext(ConfigContext);
  const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'asc' });

  const { mappedClientBranches } = useCRM();
  const { mappedBranches } = useBranches();

  const formattedData = useMemo(
    () =>
      data.map((item) => {
        return {
          ...item,
          date: moment(item.date).format(shortDateFormat),
          branchName: mappedBranches?.[item.supplierBranchId]?.name || '',
          clientName: mappedClientBranches?.[item.clientBranchId]?.name || '',
          orderStatus: Object.values(ORDER_STATUSES).find(({ label }) => label === item.status)
        };
      }),
    [data, mappedBranches, mappedClientBranches, shortDateFormat]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('date'),
        dataKey: 'date',
        formatter: (value) => value,
        sortable: true
      },
      {
        Header: t('name'),
        dataKey: 'name',
        sortable: true
      },
      showBranch && {
        Header: t('branch'),
        dataKey: 'branchName',
        sortable: true
      },
      {
        Header: t('client'),
        dataKey: 'clientName',
        sortable: true
      },
      {
        Header: t('ordersStaffNeeded'),
        dataKey: 'needed',
        minWidth: 100,
        sortable: true
      },
      {
        Header: t('orderStatus'),
        dataKey: 'orderStatus',
        minWidth: 200,
        formatter: (orderStatus) => (
          <Chip color={orderStatus?.color} background={orderStatus.background} label={t(orderStatus.slug)} />
        ),
        sortable: true
      },
      {
        Header: t('orderIssueDate'),
        dataKey: 'createdAt',
        minWidth: 150,
        formatter: (value) => moment.utc(value).local().format(`${shortDateFormat}, ${timeFormat}`),
        sortable: true
      },
      {
        Header: t('view'),
        dataKey: 'id',
        Cell: () => (
          <Button type="button" narrow tertiary style={{ background: 'transparent' }}>
            {t('view')}
          </Button>
        ),
        cellRendererProps: {
          style: {
            margin: '0 auto'
          }
        }
      }
    ],
    [shortDateFormat, showBranch, t, timeFormat]
  );

  const onRowClick = useCallback(
    ({ rowData }) => {
      const url = new URL(
        `${accountId}/scheduling/branch/${selectedBranchId}/project/${rowData.projectId}`,
        window.location.origin
      );
      url.searchParams.set('orderId', rowData.id);
      window.open(url.toString(), '_blank')?.focus();
    },
    [accountId, selectedBranchId]
  );

  const sortedData = useMemo(() => {
    return [...formattedData].sort((a, b) =>
      sort.sortDirection === 'asc' ? SORT_MAP?.[sort.sortBy]?.(a, b) : SORT_MAP?.[sort.sortBy]?.(b, a)
    );
  }, [formattedData, sort.sortDirection, sort.sortBy]);

  return (
    <Wrapper>
      <Table
        columns={columns}
        data={sortedData}
        resizable
        onRowClick={onRowClick}
        sortBy={sort.sortBy}
        sortDirection={sort.sortDirection}
        setSort={setSort}
      />
    </Wrapper>
  );
};

export default OrdersSummaryTable;
